import React, { useImperativeHandle, useRef, useState } from 'react';

import { ErrorIcon } from '@/assets/icons/error';
import { Button } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { EntryPromotionError } from '@/types/api.generated';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

import { useBetslipStore } from '../hooks/use-betslip-store';
import { BetslipPick, PlayerWithTeam } from '../types';

const modalId = 'promoRemoved';

export type PromoRemovedModalRef = {
    show: (data: PromoRemovedProps) => void;
};

export type PromoRemovedProps = {
    promoError: EntryPromotionError;
    removePromo: () => void;
    removePlayers: (players: { eventId: string; player: PlayerWithTeam }[]) => void;
    latestPick: BetslipPick | undefined;
};

export const PromoRemovedModal = React.forwardRef<PromoRemovedModalRef, {}>((_props, ref) => {
    const [data, setData] = useState<PromoRemovedProps | undefined>(undefined);
    const modalRef = useRef<BottomSheetModal>(null);

    useImperativeHandle(ref, () => ({
        show: modalData => {
            BetrAnalytics.trackEvent(AnalyticsEvent.PROMO_REMOVED_MODAL_SHOWN);
            setData(modalData);
            modalRef.current?.present();
        },
    }));

    return (
        <Modal modalRef={modalRef} id={modalId} dismissible={false} enableOverDrag={false}>
            {data ? <ModalContent {...data} modalRef={modalRef} /> : null}
        </Modal>
    );
});

const ModalContent = (
    props: PromoRemovedProps & {
        modalRef: React.RefObject<BottomSheetModal | null>;
    }
) => {
    const { modalRef, promoError, removePromo, removePlayers, latestPick } = props;
    const selectedPromo = useBetslipStore(state => state.selectedPromo);

    // Display "Keep Promo / Add pick" only when the user explicitly adds a pick (has latest pick)
    // Show "Got it" on promo errors triggered by removals (we can't restore removed picks)
    const promoCanBeKept = !!latestPick;

    return (
        <Box>
            <Box pt="s16" px="s24" pb="s32" justifyContent={'center'} alignItems="center">
                <ErrorIcon fill={ds.colorValue.background.danger.strong} />
                <SizedBox value={20} />
                <Text variant="headlineMedium" textAlign="center">
                    {promoCanBeKept ? 'Promo will be removed' : 'Promo removed'}
                </Text>
                <SizedBox value={8} />
                <Text
                    variant="bodyMedium"
                    color={ds.colorToken.text.secondary}
                    textAlign="center"
                    testID="restrictedModalDescription"
                >
                    {promoError.longDescription}
                </Text>
            </Box>

            <Box p="s16">
                {promoCanBeKept ? (
                    <>
                        <Button
                            label={'Keep promo'}
                            variant="light"
                            onPress={() => {
                                if (latestPick) {
                                    BetrAnalytics.trackEvent(AnalyticsEvent.KEEP_PROMO_FROM_REMOVED_MODAL, {
                                        promoId: selectedPromo?.promotion.id,
                                    });
                                    removePlayers([{ eventId: latestPick.eventId, player: latestPick.player }]);
                                }
                                modalRef.current?.dismiss();
                            }}
                            testID="replaceButton"
                        />
                        <SizedBox value={16} />
                        <Button
                            label={'Add pick'}
                            hierarchy="primary"
                            onPress={() => {
                                removePromo();
                                BetrAnalytics.trackEvent(
                                    AnalyticsEvent.ADD_PICK_FROM_REMOVED_MODAL,
                                    selectedPromo ? { promoId: selectedPromo.promotion.id } : undefined
                                );
                                modalRef.current?.dismiss();
                            }}
                        />
                    </>
                ) : (
                    <Button
                        label={'Got it'}
                        hierarchy="primary"
                        onPress={() => {
                            removePromo();
                            BetrAnalytics.trackEvent(
                                AnalyticsEvent.DISMISS_PROMO_REMOVED_MODAL,
                                selectedPromo ? { promoId: selectedPromo.promotion.id } : undefined
                            );
                            modalRef.current?.dismiss();
                        }}
                        testID="gotItButton"
                    />
                )}
            </Box>
        </Box>
    );
};
